<template>
  <b-modal
    :id="`schedule-articles-modal`"
    size="lg"
    hide-header-close
    no-close-on-esc
    no-close-on-backdrop
    centered
  >
    <template v-slot:modal-header>
      <h5 class="mb-0">
        Schedule Article(s) Delivery
      </h5>
    </template>
    <section>
      <div
        class="mb-4"
      >
        <p
          class="mb-5"
        >
          Choose the dates when you would like to start and stop delivering this article. The campaign's start and end dates are selected by default.
        </p>
        <p class="mb-0">
          Delivery will start at 0:00 UTC on the start date and stop at 23:59 UTC of the end date.
        </p>
        <br>
        <p class="mb-0">
          Scheduling will not affect articles with the states <b>FAILED</b>, <b>PUBLISHED</b>, <b>SCHEDULED</b> or <b>REVIEW</b>, nor articles with <b>Poor Matching Health</b>.
        </p>
      </div>
      <b-row
        class="gutters-0 new-gutters-xs"
      >
        <b-col
          cols="auto"
        >
          <b-form-group>
            <template v-slot:label>
              <p
                class="mb-0 font-weight-bold"
              >
                Start Date
              </p>
            </template>
            <datepicker
              :value="startDateValue"
              @selected="setCustomStartDate($event)"
              placeholder="Select Date"
              format="dd/MM/yyyy"
              :open-date="new Date(startDateValue)"
              :disabled-dates="disabledDates"
              :bootstrap-styling="true"
              :monday-first="true"
              :inline="true"
              data-test="schedule-articles-start-date"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="auto"
        >
          <b-form-group>
            <template v-slot:label>
              <p
                class="mb-0 font-weight-bold"
              >
                End Date (optional)
              </p>
            </template>
            <datepicker
              :value="endDateValue"
              @selected="setCustomEndDate($event)"
              placeholder="Select Date"
              format="dd/MM/yyyy"
              :open-date="endDateValue ? new Date(endDateValue) : new Date(maxEndDate)"
              :disabled-dates="disabledDates"
              :bootstrap-styling="true"
              :monday-first="true"
              :inline="true"
              data-test="schedule-articles-end-date"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </section>
    <template v-slot:modal-footer>
      <b-button
        variant="secondary"
        @click="clearSchedule()"
        :disabled="!canPublishArticles({ ids: selectedIds })"
        data-test="schedule-articles-btn-clear-schedule"
      >
        Clear schedule
      </b-button>
      <div class="ml-auto">
        <b-button
          variant="tertiary"
          class="mr-2"
          @click="closeModal()"
          data-test="schedule-articles-btn-cancel"
        >
          Cancel
        </b-button>
        <b-button
          variant="primary"
          :class="{ 'btn-loading': isScheduleButtonLoading }"
          @click="scheduleArticles()"
          :disabled="!canPublishArticles({ ids: selectedIds })"
          data-test="schedule-articles-btn-confirm"
        >
          Schedule
        </b-button>
      </div>
    </template>
  </b-modal>
</template>
<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import * as campaignTypes from '@/modules/campaigns/store/types';
import * as contentTypes from '@/modules/content/store/types';

import Datepicker from 'vuejs-datepicker';
import dateHelper from '@/core/mixins/dateHelper';

import ArticlesActionsMixins from '@/modules/content/components/ContentPage/mixins/ArticlesActionsMixins';

function initialData() {
  return {
    isScheduleButtonLoading: false,
    today: new Date(),
    selectedStartDate: null,
    selectedEndDate: null,
  };
}
export default {
  name: 'ScheduleArticlesModal',
  mixins: [
    dateHelper,
    ArticlesActionsMixins,
  ],
  components: {
    Datepicker,
  },
  props: {
    customProps: {
      type: Object,
      required: true,
      default: () => {},
    },
  },
  data() {
    return initialData();
  },
  computed: {
    ...mapState('campaigns', [
      'selectedAdvertiser',
    ]),
    ...mapState('content', [
      'feedId',
    ]),
    ...mapGetters('campaigns', [
      campaignTypes.GET_CAMPAIGN_MIN_START_DATE,
      campaignTypes.GET_CAMPAIGN_MAX_END_DATE,
    ]),
    ...mapGetters('content', [
      contentTypes.GET_ARTICLE_BY_ID,
    ]),
    selectedIds() {
      if (!this.customProps.hasOwnProperty('ids')) return [];
      return this.customProps.ids;
    },
    isBulk() {
      if (!this.customProps.hasOwnProperty('isBulk')) return false;
      return this.customProps.isBulk;
    },
    minSrartDate() {
      if (!this.isBulk || this.selectedIds.length === 1) {
        const article = this.GET_ARTICLE_BY_ID(this.selectedIds[0]);
        if (article?.schedule?.hasOwnProperty('start') && article?.schedule?.start) return article.schedule.start;
      }
      return new Date(this.GET_CAMPAIGN_MIN_START_DATE) > this.today ? this.GET_CAMPAIGN_MIN_START_DATE : this.convertDateToString(this.today);
    },
    maxEndDate() {
      if (!this.isBulk || this.selectedIds.length === 1) {
        const article = this.GET_ARTICLE_BY_ID(this.selectedIds[0]);
        if (article?.schedule?.hasOwnProperty('stop') && article?.schedule?.stop) return article.schedule.stop;
      }
      return this.GET_CAMPAIGN_MAX_END_DATE;
    },
    startDateValue() {
      return this.selectedStartDate ? this.convertDateToString(this.selectedStartDate) : this.minSrartDate;
    },
    endDateValue() {
      if (this.selectedEndDate) return this.convertDateToString(this.selectedEndDate);
      if (this.maxEndDate !== this.GET_CAMPAIGN_MAX_END_DATE) return this.maxEndDate;
      return null;
    },
    disabledDates() {
      return {
        to: this.minSrartDate ? new Date(this.minSrartDate) : null,
        from: this.maxEndDate ? new Date(this.maxEndDate) : this.today,
        customPredictor: () => !this.canPublishArticles({
          ids: this.selectedIds,
        }),
      };
    },
  },
  methods: {
    ...mapActions('content', [
      contentTypes.SET_CONTENT_ITEM_PUBLISHING_STATE,
    ]),
    setCustomStartDate(date) {
      this.selectedStartDate = date;
      if (this.endDateValue && date > new Date(this.endDateValue)) this.selectedEndDate = date;
    },
    setCustomEndDate(date) {
      this.selectedEndDate = date;
      if (this.startDateValue && date < new Date(this.startDateValue)) this.selectedStartDate = date;
    },
    async scheduleArticles() {
      this.isScheduleButtonLoading = true;
      const payload = {
        feedId: this.feedId,
        advertiserId: this.selectedAdvertiser.id,
        contentItemIds: this.selectedIds,
        action: 'SCHEDULE',
        schedule: {
          start: this.startDateValue,
          stop: this.endDateValue,
        },
      };
      if (this.endDateValue === this.GET_CAMPAIGN_MAX_END_DATE) delete payload.schedule.stop;
      try {
        await this.SET_CONTENT_ITEM_PUBLISHING_STATE(payload);
        this.$emit('confirmAction');
      } finally {
        this.closeModal();
      }
    },
    clearSchedule() {
      this.selectedStartDate = null;
      this.selectedEndDate = null;
    },
    resetData() {
      Object.assign(this.$data, initialData());
    },
    closeModal({ fetchArticles = false } = {}) {
      this.resetData();
      this.$root.$emit('bv::hide::modal', 'schedule-articles-modal');
      if (fetchArticles) this.$emit('fetchArticles');
    },
  },
};
</script>
<style lang="scss" scoped>
::v-deep {
  .modal-dialog{
    max-width: 650px;
  }
}
</style>
